import React from 'react'
import classNames from 'classnames'
import {
  DataGrid as MuiDataGrid,
  DataGridProps as MuiDataGridProps,
  makeStyles,
} from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'

const useDataGridStyles = makeStyles(({ spacing, typography }) => ({
  root: {
    '& .MuiDataGrid-root .MuiDataGrid-row:hover': {
      backgroundColor: brandColors.transparent,
    },

    '& .MuiDataGrid-root': {
      border: 0,
    },

    '& .MuiDataGrid-columnHeaderTitleContainer': {
      padding: 0,
      display: 'flex',
      justifyContent: 'space-between',
    },

    '& .MuiDataGrid-columnHeaderTitle': {
      fontWeight: typography.fontWeightBold,
    },

    '& .MuiDataGrid-columnsContainer': {
      borderBottomColor: brandColors.coolGray3,
      color: brandColors.coolGray5,
    },

    '& .MuiDataGrid-columnSeparator': {
      display: 'none',
    },

    '& .MuiDataGrid-columnHeader, & .MuiDataGrid-cell': {
      boxShadow: 'none',
      border: 0,
      padding: spacing(0, 1),

      '&:focus': {
        outline: 'none',
      },
    },

    '& .MuiInputBase-input': {
      border: '0 !important', // Override MUI heavy selector
    },

    '& .MuiDataGrid-cell': {
      borderBottom: `1px solid ${brandColors.transparent}`,
    },

    '& .MuiDataGrid-row': {
      borderBottom: `1px solid ${brandColors.coolGray3}`,
    },

    '& .MuiDataGrid-row:last-child': {
      borderBottom: 'none',
    },

    '& .MuiDataGrid-root .MuiDataGrid-row:last-child .MuiDataGrid-cell--textLeft': {
      borderBottom: 'none',
    },

    '& .MuiTablePagination-input': {
      width: 'auto',
    },
  },
  dataGridEditMode: {
    '& .MuiDataGrid-root .MuiDataGrid-row:hover': {
      backgroundColor: brandColors.gray0,
    },

    '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
      boxShadow: `0 0 0 3px ${brandColors.skyBlue2}`,
      border: `1px solid ${brandColors.skyBlue6}`,
    },

    '& .MuiDataGrid-editInputCell': {
      padding: 0,
    },

    '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': {
      outline: `1px solid ${brandColors.skyBlue6}`,
    },
  },
}))

export interface IDataGridProps extends MuiDataGridProps {
  /**
   * Determines whether the DataGrid is editable or not.
   * @default false
   */
  editable?: boolean
  /**
   * Controls the height of the DataGrid component automatically.
   * @default true
   */
  autoHeight?: boolean
}

const DataGrid = ({ autoHeight = true, editable = false, ...rest }: IDataGridProps) => {
  const classes = useDataGridStyles({ editable })
  const className = classNames(classes.root, { [classes.dataGridEditMode]: editable })

  return (
    <div className={className} data-test='DataGrid'>
      <MuiDataGrid autoHeight={autoHeight} {...rest} />
    </div>
  )
}

export default DataGrid
